<template>
  <div class="cfg-tab">
    <div class="cfg-intro">
      <v-icon>mdi-information-outline</v-icon>
      <div>
        <p>
          Structura universității. Redenumirea păstrează demersurile depuse sub denumirea veche (membrii le văd în
          continuare); „Versiune nouă” creează un departament nou și îl ascunde pe cel vechi, fără să mute nimic (cei
          din cel nou văd și demersurile celui vechi).
        </p>
        <p>Un departament ascuns nu mai poate fi ales pentru nimic nou; ce există rămâne.</p>
        <p>
          Demersurile unui departament le aprobă Șeful lui. O subdiviziune fără Șef propriu (de ex. un laborator sub o
          catedră) le trimite Șefului departamentului de deasupra; cu un Șef propriu, aprobă el în locul lui.
        </p>
      </div>
    </div>

    <div class="cfg-toolbar">
      <h3 class="panel-title">Departamente <span class="count">{{ departments.length }}</span></h3>
      <span class="grow-spacer" />
      <v-text-field
        v-model="search"
        class="cfg-search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Caută un departament"
        aria-label="Caută un departament"
        outlined
        dense
        single-line
        hide-details
        clearable
      ></v-text-field>
      <v-btn class="btn-green" @click="openDialog('create', null, rootId)">
        <v-icon left>mdi-plus</v-icon>Departament nou
      </v-btn>
    </div>

    <div class="cfg-chips deps__view">
      <button type="button" class="cfg-chip" :class="{ 'is-active': !onlyHidden }" :aria-pressed="!onlyHidden" @click="onlyHidden = false">
        <v-icon small>mdi-file-tree-outline</v-icon><span class="cfg-chip__name">Structura</span>
      </button>
      <button
        type="button"
        class="cfg-chip"
        :class="{ 'is-active': onlyHidden }"
        :aria-pressed="onlyHidden"
        @click="onlyHidden = true"
      >
        <v-icon small>mdi-eye-off-outline</v-icon><span class="cfg-chip__name">Ascunse</span>
        <span class="cfg-chip__count">{{ hiddenCount }}</span>
      </button>
      <template v-if="!flat">
        <v-btn text small class="deps__tree-btn" @click="expandAll"><v-icon left small>mdi-unfold-more-horizontal</v-icon>Extinde tot</v-btn>
        <v-btn text small class="deps__tree-btn" @click="expanded = {}"><v-icon left small>mdi-unfold-less-horizontal</v-icon>Restrânge tot</v-btn>
      </template>
    </div>

    <v-progress-linear v-if="loading && !loaded" indeterminate color="primary" class="mb-2" />
    <div class="cfg-list">
      <div v-if="loaded && !rows.length" class="cfg-empty">
        {{ flat ? "Niciun departament găsit." : "Niciun departament." }}
      </div>
      <div
        v-for="row in rows"
        :key="row.d.id"
        class="cfg-row deps__row"
        :class="{ 'is-hidden': row.d.is_hidden }"
        :style="{ '--depth': row.depth }"
      >
        <v-btn
          v-if="!flat && hasChildren(row.d)"
          icon
          small
          class="deps__toggle"
          :aria-label="expanded[row.d.id] ? 'Restrânge' : 'Extinde'"
          :aria-expanded="String(!!expanded[row.d.id])"
          @click="toggle(row.d)"
        >
          <v-icon>{{ expanded[row.d.id] ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        </v-btn>
        <span v-else-if="!flat" class="deps__leaf" aria-hidden="true"></span>

        <div class="cfg-row__main">
          <div class="cfg-row__title">
            <span class="cfg-row__name">{{ row.d.name }}</span>
            <span v-if="row.d.is_hidden" class="cfg-tag"><v-icon>mdi-eye-off-outline</v-icon>Ascuns</span>
            <span v-if="row.orphan" class="cfg-tag cfg-tag--amber" title="Părintele nu mai există">fără părinte</span>
          </div>
          <div v-if="flat && paths[row.d.id]" class="cfg-row__sub deps__path">în {{ paths[row.d.id] }}</div>
          <div v-if="row.d.former_names && row.d.former_names.length" class="cfg-row__sub">
            <span class="deps__former-label">Denumiri anterioare:</span> {{ row.d.former_names.join(", ") }}
          </div>
          <button
            type="button"
            class="deps__heads"
            :class="`deps__heads--${headsTone(row.d)}`"
            :title="'Cine aprobă demersurile: ' + headsText(row.d)"
            @click="openHeads(row.d)"
          >
            <v-icon small>{{ headsIcon(row.d) }}</v-icon>
            <span>{{ headsText(row.d) }}</span>
          </button>
          <div class="cfg-usage">{{ usageText(row.d.usage) || "Nefolosit" }}</div>
        </div>

        <div class="cfg-row__actions">
          <v-btn icon color="primary" aria-label="Cine aprobă demersurile" title="Cine aprobă demersurile" @click="openHeads(row.d)">
            <v-icon>mdi-account-tie-outline</v-icon>
          </v-btn>
          <v-btn icon color="primary" aria-label="Redenumește" title="Redenumește" @click="openDialog('rename', row.d)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon aria-label="Mai multe acțiuni" v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <!-- a hidden department is not offered for anything new, sub-units included -->
              <v-list-item v-if="!row.d.is_hidden" @click="openDialog('create', null, row.d.id)">
                <v-list-item-icon><v-icon>mdi-plus</v-icon></v-list-item-icon>
                <v-list-item-title>Adaugă subdiviziune</v-list-item-title>
              </v-list-item>
              <template v-if="!isRoot(row.d)">
                <v-list-item @click="openDialog('move', row.d)">
                  <v-list-item-icon><v-icon>mdi-folder-move-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Mută sub alt departament</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDialog('replace', row.d)">
                  <v-list-item-icon><v-icon>mdi-file-replace-outline</v-icon></v-list-item-icon>
                  <v-list-item-title>Versiune nouă</v-list-item-title>
                </v-list-item>
                <v-list-item :disabled="saving" @click="toggleHidden(row.d)">
                  <v-list-item-icon>
                    <v-icon>{{ row.d.is_hidden ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ row.d.is_hidden ? "Arată din nou" : "Ascunde" }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="remove(row.d)">
                  <v-list-item-icon><v-icon color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                  <v-list-item-title class="deps__danger">Șterge</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </div>
      </div>
    </div>

    <DepartmentDialog
      ref="dialog"
      :departments="sortedDepartments"
      :paths="paths"
      :descendants="descendantsOf"
      :saving="saving"
      @submit="submit"
    />
    <DepartmentHeadsDialog ref="heads" :by-id="byId" :saving="saving" @submit="saveHeads" @error="$emit('error', $event)" />
    <ConfirmUsedDialog ref="confirmUsed" />
    <RemoveDialog ref="remove" />
  </div>
</template>

<script>
import * as api from "./api";
import configMixin from "./configMixin";
import ConfirmUsedDialog from "./ConfirmUsedDialog.vue";
import RemoveDialog from "./RemoveDialog.vue";
import DepartmentDialog from "./DepartmentDialog.vue";
import DepartmentHeadsDialog from "./DepartmentHeadsDialog.vue";
import { plain, usageText } from "./format";

const ROOT_ID = 1;
const byName = (a, b) => a.name.localeCompare(b.name, "ro", { numeric: true }) || a.id - b.id;

export default {
  name: "DepartmentsTab",
  components: { ConfirmUsedDialog, RemoveDialog, DepartmentDialog, DepartmentHeadsDialog },
  mixins: [configMixin],
  data() {
    return {
      departments: [],
      rootId: ROOT_ID,
      search: "",
      onlyHidden: false,
      // id -> true for the open branches; the university is open at the start
      expanded: { [ROOT_ID]: true },
    };
  },
  computed: {
    sortedDepartments() {
      return [...this.departments].sort(byName);
    },
    byId() {
      return new Map(this.departments.map((d) => [d.id, d]));
    },
    childrenOf() {
      const map = new Map();
      this.sortedDepartments.forEach((d) => {
        if (!map.has(d.parent_id)) map.set(d.parent_id, []);
        map.get(d.parent_id).push(d);
      });
      return map;
    },
    // the university, then departments whose parent no longer exists
    roots() {
      return this.sortedDepartments
        .filter((d) => !d.parent_id || !this.byId.has(d.parent_id))
        .sort((a, b) => Number(b.id === ROOT_ID) - Number(a.id === ROOT_ID));
    },
    paths() {
      const paths = {};
      this.departments.forEach((d) => {
        const names = [];
        const seen = new Set([d.id]);
        let parent = this.byId.get(d.parent_id);
        while (parent && !seen.has(parent.id)) {
          names.unshift(parent.name);
          seen.add(parent.id);
          parent = this.byId.get(parent.parent_id);
        }
        paths[d.id] = names.join(" › ");
      });
      return paths;
    },
    hiddenCount() {
      return this.departments.filter((d) => d.is_hidden).length;
    },
    flat() {
      return this.onlyHidden || !!plain(this.search);
    },
    rows() {
      if (this.flat) {
        const term = plain(this.search);
        return this.sortedDepartments
          .filter((d) => !this.onlyHidden || d.is_hidden)
          .filter((d) => !term || [d.name, ...(d.former_names || [])].some((n) => plain(n).includes(term)))
          .map((d) => ({ d, depth: 0 }));
      }
      const rows = [];
      const seen = new Set();
      const walk = (d, depth) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        rows.push({ d, depth, orphan: d.parent_id > 0 && !this.byId.has(d.parent_id) });
        if (this.expanded[d.id]) (this.childrenOf.get(d.id) || []).forEach((c) => walk(c, depth + 1));
      };
      this.roots.forEach((d) => walk(d, 0));
      return rows;
    },
  },
  methods: {
    usageText,
    async load() {
      this.departments = await api.getDepartments();
    },
    isRoot(d) {
      return d.id === ROOT_ID;
    },
    hasChildren(d) {
      return (this.childrenOf.get(d.id) || []).length > 0;
    },
    toggle(d) {
      this.$set(this.expanded, d.id, !this.expanded[d.id]);
    },
    expandAll() {
      const all = {};
      this.departments.forEach((d) => {
        if (this.hasChildren(d)) all[d.id] = true;
      });
      this.expanded = all;
    },
    descendantsOf(id) {
      const found = new Set();
      const stack = [id];
      while (stack.length) {
        (this.childrenOf.get(stack.pop()) || []).forEach((c) => {
          if (!found.has(c.id)) {
            found.add(c.id);
            stack.push(c.id);
          }
        });
      }
      return found;
    },
    openDialog(mode, dept, parentId) {
      this.$refs.dialog.open(mode, dept, parentId);
    },
    // own: its Șefs; inherited: those of the nearest department above; none: nobody would see its requests
    headsTone(d) {
      const a = d.approvers;
      if (!a || !a.heads.length) return d.usage && (d.usage.members || d.usage.openRequests) ? "none" : "empty";
      return a.from_id === d.id ? "own" : "inherited";
    },
    headsIcon(d) {
      return { own: "mdi-account-tie", inherited: "mdi-arrow-up-bold-circle-outline", none: "mdi-alert-outline", empty: "mdi-account-off-outline" }[this.headsTone(d)];
    },
    headsText(d) {
      const a = d.approvers;
      if (!a || !a.heads.length) return this.headsTone(d) === "none" ? "Fără Șef: demersurile nu le aprobă nimeni" : "Fără Șef";
      const people = a.heads.map((h) => h.name).join(", ");
      if (a.from_id === d.id) return `Șef: ${people}`;
      const from = this.byId.get(a.from_id);
      return `Aprobă: ${people} · prin „${from ? from.name : "departamentul de deasupra"}”`;
    },
    openHeads(d) {
      this.$refs.heads.open(d);
    },
    async saveHeads({ dept, user_ids }) {
      const ok = await this.change(() => api.setDepartmentHeads(dept.id, user_ids), {
        done: user_ids.length
          ? `Șefii departamentului „${dept.name}” au fost salvați`
          : `„${dept.name}” nu mai are Șef propriu: demersurile urcă la Șeful de deasupra`,
        fallback: "Eroare la salvarea șefilor",
      });
      if (ok) this.$refs.heads.close();
    },
    async submit({ mode, dept, name, parent_id }) {
      let ok = false;
      if (mode === "create") {
        ok = await this.change(() => api.createDepartment({ name, parent_id }), {
          done: `Departamentul „${name}” a fost adăugat`,
          fallback: "Eroare la adăugarea departamentului",
        });
      } else if (mode === "rename") {
        if (name === dept.name) return this.$refs.dialog.close();
        ok = await this.change((extra) => api.updateDepartment(dept.id, { name, ...extra }), {
          done: `Departamentul a fost redenumit în „${name}”`,
          fallback: "Eroare la redenumirea departamentului",
          confirm: {
            title: `Redenumiți departamentul „${dept.name}”?`,
            from: dept.name,
            to: name,
            typeName: true,
            changes: [
              "Echipamentele departamentului, listele de alegere și utilizatorii arată denumirea nouă.",
              "Membrii care au acest departament ca principal trec la denumirea nouă.",
            ],
            keeps: [
              "Demersurile depuse păstrează denumirea cu care au fost depuse: sunt documente.",
              "Membrii văd în continuare demersurile depuse sub denumirea veche; ea rămâne la „Denumiri anterioare”.",
              "Nimic nu se mută: membrii, echipamentele și demersurile rămân la același departament.",
            ],
          },
        });
      } else if (mode === "move") {
        if (parent_id === dept.parent_id) return this.$refs.dialog.close();
        const parent = this.byId.get(parent_id);
        ok = await this.change(() => api.updateDepartment(dept.id, { parent_id }), {
          done: `„${dept.name}” a fost mutat sub „${parent ? parent.name : parent_id}”`,
          fallback: "Eroare la mutarea departamentului",
        });
      } else if (mode === "replace") {
        ok = await this.change(() => api.replaceDepartment(dept.id, { name, parent_id }), {
          done: `Versiunea nouă „${name}” a fost creată, iar „${dept.name}” este ascuns. Mutați manual oamenii și echipamentele.`,
          fallback: "Eroare la crearea versiunii noi",
        });
      }
      if (!ok) return;
      this.$refs.dialog.close();
      // show where the result landed
      if (parent_id && mode !== "rename") this.$set(this.expanded, parent_id, true);
    },
    toggleHidden(d) {
      const hide = !d.is_hidden;
      return this.change(() => api.updateDepartment(d.id, { is_hidden: hide }), {
        done: hide
          ? `Departamentul „${d.name}” este ascuns: nu mai poate fi ales pentru nimic nou`
          : `Departamentul „${d.name}” poate fi ales din nou`,
        fallback: "Eroare la salvarea departamentului",
      });
    },
    async remove(d) {
      const canDelete = !d.used && !(d.usage && d.usage.children);
      const choice = await this.$refs.remove.ask({
        title: canDelete ? `Ștergeți departamentul „${d.name}”?` : `Departamentul „${d.name}” nu poate fi șters`,
        text: canDelete
          ? "Nu are membri, echipamente, demersuri sau subdiviziuni, deci poate fi șters."
          : `Are ${usageText(d.usage)}. ` +
            (d.is_hidden
              ? "Este deja ascuns, deci nu mai poate fi ales pentru nimic nou."
              : "Ascundeți-l: nu va mai putea fi ales pentru nimic nou, iar ce există rămâne neschimbat."),
        canDelete,
        canHide: !canDelete && !d.is_hidden,
      });
      if (choice === "delete") {
        await this.change(() => api.deleteDepartment(d.id), {
          done: `Departamentul „${d.name}” a fost șters`,
          fallback: "Eroare la ștergerea departamentului",
        });
      } else if (choice === "hide") {
        await this.toggleHidden(d);
      }
    },
  },
};
</script>

<style scoped>
.deps__view {
  align-items: center;
}
.deps__view .cfg-chip .v-icon {
  color: inherit;
}
.deps__tree-btn.v-btn {
  color: var(--ink-2);
}
.cfg-row.deps__row {
  padding-left: calc(8px + var(--depth, 0) * 22px);
  gap: 6px 10px;
}
.deps__toggle.v-btn {
  flex: none;
  margin-top: -2px;
}
.deps__leaf {
  flex: none;
  width: 28px;
}
.deps__path {
  color: var(--ink-3);
}
.deps__former-label {
  font-weight: 700;
  color: var(--ink-2);
}
.deps__danger {
  color: var(--brick);
}
.deps__heads {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
  margin-top: 6px;
  padding: 2px 10px 2px 8px;
  border-radius: 999px;
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
}
.deps__heads span {
  overflow-wrap: anywhere;
}
.deps__heads .v-icon {
  flex: none;
  margin-top: 1px;
  color: inherit;
}
.deps__heads:hover {
  text-decoration: underline;
}
.deps__heads--own {
  background: var(--mint-50);
  color: var(--green-700);
  font-weight: 600;
}
.deps__heads--inherited {
  background: var(--sky-bg);
  color: var(--sky);
}
.deps__heads--none {
  background: var(--amber-bg);
  color: var(--amber);
  font-weight: 600;
}
.deps__heads--empty {
  color: var(--ink-3);
  padding-left: 0;
}
@media (max-width: 600px) {
  .cfg-row.deps__row {
    padding-left: calc(4px + var(--depth, 0) * 14px);
  }
}
</style>
